/**
 * Button Component
 *
 * Variants: primary, secondary, accent, ghost, destructive, outline
 * Sizes: sm, md, lg
 *
 * WCAG 2.1 AA compliant with minimum 44x44px touch targets
 */

'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const buttonVariants = cva(
  // Base styles
  'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--color-primary-500)] text-white hover:bg-[var(--color-primary-600)] focus-visible:ring-[var(--color-primary-500)]',
        secondary: 'bg-[var(--color-secondary-100)] text-[var(--color-secondary-700)] hover:bg-[var(--color-secondary-200)] focus-visible:ring-[var(--color-secondary-500)]',
        accent: 'bg-[var(--color-accent-500)] text-white hover:bg-[var(--color-accent-600)] focus-visible:ring-[var(--color-accent-500)]',
        ghost: 'bg-transparent text-[var(--color-secondary-600)] hover:bg-[var(--color-secondary-100)] focus-visible:ring-[var(--color-secondary-500)]',
        destructive: 'bg-[var(--color-error-500)] text-white hover:bg-[#DC2626] focus-visible:ring-[var(--color-error-500)]',
        outline: 'border border-[var(--color-border-default)] text-[var(--color-secondary-700)] hover:bg-[var(--color-secondary-50)] focus-visible:ring-[var(--color-secondary-500)]',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-base',
        lg: 'h-12 px-6 text-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
